import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Lock, Volume2, VolumeX, Play, Pause, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { debug, error as logError } from '../../utils/logger';
import { DjayNarrator, BrandLockup } from '../../mascot';
interface WelcomeScreenProps {
  onLoadDemo: () => void;
  isLoadingDemo?: boolean;
  onLogin: () => void;
  onRegister: () => void;
}
export function WelcomeScreen({
  onLoadDemo,
  isLoadingDemo = false,
  onLogin,
  onRegister
}: WelcomeScreenProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted as requested
  const [isPlaying, setIsPlaying] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = 0.6;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Browser blocked autoplay with audio. Mute it and play, show unmute prompt.
          debug('Autoplay with audio blocked, falling back to muted autoplay.');
          setIsMuted(true);
          setAutoplayBlocked(true);
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(e => logError('Even muted autoplay failed', e));
          }
        });
      }
    }
  }, []);
  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      if (autoplayBlocked && !newMutedState) {
        setAutoplayBlocked(false);
      }
    }
  };
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return <div className="min-h-screen bg-[#FFF9F2] flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden font-sans" data-id="element-514" data-mp="lu8xbdkrccgy">
      {/* Subtle Ambient Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-515" data-mp="zrfgh99e8d6p">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-orange-100/50 blur-[120px]" data-id="element-516" data-mp="vqc2ksawor84" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-amber-100/50 blur-[120px]" data-id="element-517" data-mp="33rcdv662l65" />
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20 w-full max-w-7xl relative z-10" data-id="element-518" data-mp="gvxnv3ydk3z9">
        {/* Left Column: Content & CTAs */}
        <div className="w-full max-w-lg flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-519" data-mp="3kjh9rpgnbr7p">
          <motion.div className="mb-8 lg:mb-10" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-520" data-mp="3gj74kf0oh4tw">
            {/* The BrandLockup is now the singular hero brand element */}
            <BrandLockup size="xl" className="drop-shadow-md" data-id="element-521" data-mp="31g5p9ofrt82p" />
          </motion.div>

          <motion.div className="mb-10 relative" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-522" data-mp="3p4otnsvp90pi">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.1] mb-5" data-id="element-523" data-mp="23rvkx2sii88f">
              Leren wordt een{' '}
              <span className="text-orange-500" data-id="element-524" data-mp="28s9regegta4c">groot avontuur!</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 font-medium max-w-md mx-auto lg:mx-0 leading-relaxed" data-id="element-525" data-mp="2oyms1dkl2r8e">
              Oefen rekenen, taal, spelling en meer — helemaal op jouw eigen
              niveau met Meester Djay.
            </p>
          </motion.div>

          <motion.div className="w-full space-y-4 max-w-md" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4,
          ease: 'easeOut'
        }} data-id="element-526" data-mp="2a9gtzsxhf723">
            <Button variant="primary" size="xl" className="w-full text-lg shadow-lg hover:shadow-xl transition-shadow" onClick={onRegister} disabled={isLoadingDemo} data-id="element-527" data-mp="2szge01egtvyn">
              Start je avontuur
              <ArrowRight className="w-6 h-6 ml-2" data-id="element-528" data-mp="1hmmelafiuxvh" />
            </Button>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-529" data-mp="3t97sf179a63r">
              <Button variant="outline" size="lg" className="w-full sm:w-1/2 bg-white/80 backdrop-blur-sm border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300 transition-colors" onClick={onLoadDemo} disabled={isLoadingDemo} aria-busy={isLoadingDemo} data-id="element-530" data-mp="20k5igtdaau3">
                {isLoadingDemo ? <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-531" data-mp="kq0abgc34pgb" />
                    Laden…
                  </> : <>
                    <Sparkles className="w-5 h-5 mr-2" data-id="element-532" data-mp="uoy3wxodb966" />
                    Probeer demo
                  </>}
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-1/2 bg-white/80 backdrop-blur-sm border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors" onClick={onLogin} disabled={isLoadingDemo} data-id="element-533" data-mp="2rkm474zgl9z2">
                Log in
              </Button>
            </div>
          </motion.div>

          <motion.div className="mt-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} data-id="element-534" data-mp="2ychokuhuo2za">
            <p className="text-sm text-slate-500 font-medium flex items-center justify-center lg:justify-start gap-2" data-id="element-535" data-mp="33bumcaa9eugn">
              <Lock className="w-4 h-4 text-emerald-500" data-id="element-536" data-mp="3c68y42vkzmv0" />
              100% Veilig & lokaal opgeslagen
            </p>
          </motion.div>
        </div>

        {/* Right Column: Video */}
        <motion.div className="w-full max-w-md lg:max-w-lg relative" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-537" data-mp="3r15dd01t5ahl">
          {/* Video Player Container - Cleaned up, no extra mascots */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-orange-50 group ring-1 ring-slate-900/5" data-id="element-538" data-mp="4komndgo1yv1">
            {!hasVideoError ? <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/qpe4tybRX3NWaXWg6AM6tr/gemini_generated_video_ffdfec60.mp4" loop playsInline className="w-full h-auto object-cover aspect-[9/16] sm:aspect-auto" onClick={togglePlay} onError={() => {
            debug('Video failed to load, falling back to illustration.');
            setHasVideoError(true);
          }} data-id="element-539" data-mp="3qthhvs2yh0il" /> : <div className="w-full aspect-[9/16] sm:aspect-video bg-gradient-to-br from-orange-50 to-amber-100 flex flex-col items-center justify-center p-8" data-id="element-540" data-mp="3njoivm1wrr7i">
                <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-541" data-mp="3ej058sgffsv5">
                  <DjayNarrator size="xl" emotion="excited" animate={true} data-id="element-542" data-mp="2zun5dke7xpvd" />
                </motion.div>
                <div className="mt-6 text-center" data-id="element-543" data-mp="2vud24fht9dk7">
                  <h3 className="text-xl font-bold text-orange-900 mb-2" data-id="element-544" data-mp="2vqxkb4wr5qf3">
                    Klaar voor de start!
                  </h3>
                  <p className="text-orange-800/80 font-medium" data-id="element-545" data-mp="2lvdpymxu6zsg">
                    Jouw leeravontuur begint hier.
                  </p>
                </div>
              </div>}

            {/* Video Controls Overlay */}
            {!hasVideoError && <div className="absolute bottom-4 right-4 z-30 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus-within:opacity-100" data-id="element-546" data-mp="2br73zs8iw9zm">
                <button onClick={togglePlay} className="bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/50" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-547" data-mp="2gr1uhuodyxk5">
                  {isPlaying ? <Pause className="w-5 h-5" data-id="element-548" data-mp="1xz8zxvto54ab" /> : <Play className="w-5 h-5 ml-0.5" data-id="element-549" data-mp="1xxy0g879aip0" />}
                </button>
                <button onClick={toggleMute} className="bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/50" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-550" data-mp="22sbua669shrk">
                  {isMuted ? <VolumeX className="w-5 h-5" data-id="element-551" data-mp="3ffvntstdb03m" /> : <Volume2 className="w-5 h-5" data-id="element-552" data-mp="3kfqh230m81ru" />}
                </button>
              </div>}

            {/* Autoplay Blocked Overlay */}
            {autoplayBlocked && <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-id="element-553" data-mp="2rsdsjnaw9jvw">
                <button onClick={toggleMute} className="bg-white text-slate-900 hover:bg-orange-50 px-6 py-3 rounded-full font-bold flex items-center gap-2 shadow-xl transform transition hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-500/50" data-id="element-554" data-mp="2wrgxl7qvjq3v">
                  <Volume2 className="w-5 h-5 text-orange-500" data-id="element-555" data-mp="290upjggve5tv" />
                  Zet geluid aan
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}